import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'
import { format } from 'date-fns'
import { listOptOuts, addOptOut, deleteOptOut } from '@/lib/api'
import type { OptOut } from '@/schemas'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const addOptOutSchema = z
  .object({
    email: z.string().email().optional().or(z.literal('')),
    linkedin_url: z.string().url().optional().or(z.literal('')),
    reason: z.string().min(1, 'Reason is required'),
    source: z.enum(['manual', 'user_reply', 'bounce_permanent', 'gdpr_request']),
  })
  .refine((d) => d.email || d.linkedin_url, {
    message: 'Provide at least an email or LinkedIn URL.',
    path: ['email'],
  })

type AddOptOutForm = z.infer<typeof addOptOutSchema>

const SOURCE_LABEL: Record<OptOut['source'], string> = {
  manual: 'Manual',
  user_reply: 'User reply',
  bounce_permanent: 'Permanent bounce',
  gdpr_request: 'GDPR request',
}

export function OptOuts() {
  const qc = useQueryClient()
  const [open, setOpen] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['opt-outs'],
    queryFn: listOptOuts,
  })

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm<AddOptOutForm>({
    resolver: zodResolver(addOptOutSchema),
    defaultValues: { source: 'manual', email: '', linkedin_url: '', reason: '' },
  })

  const addMutation = useMutation({
    mutationFn: (form: AddOptOutForm) =>
      addOptOut({
        email: form.email || undefined,
        linkedin_url: form.linkedin_url || undefined,
        reason: form.reason,
        source: form.source,
      }),
    onSuccess: () => {
      toast.success('Opt-out added.')
      qc.invalidateQueries({ queryKey: ['opt-outs'] })
      setOpen(false)
      reset()
    },
    onError: () => toast.error('Failed to add opt-out.'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteOptOut(id),
    onSuccess: () => {
      toast.success('Opt-out removed.')
      qc.invalidateQueries({ queryKey: ['opt-outs'] })
    },
    onError: () => toast.error('Could not remove opt-out.'),
  })

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Opt-outs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            People who have requested no further contact.
          </p>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}>
          <Plus className="h-4 w-4" />
          Add opt-out
        </Button>
      </div>

      <div className="rounded-lg border bg-card">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : !data?.opt_outs.length ? (
          <div className="py-14 text-center text-sm text-muted-foreground">
            No opt-outs on record.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">
                    LinkedIn URL
                  </th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Source</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Reason</th>
                  <th className="px-3 py-2 text-left font-medium text-muted-foreground">Date</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {data.opt_outs.map((oo) => (
                  <tr
                    key={oo.opt_out_id}
                    className="border-b last:border-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-sm">{oo.email ?? '—'}</td>
                    <td className="px-3 py-2.5 font-mono text-xs truncate max-w-[180px]">
                      {oo.linkedin_url ?? '—'}
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge variant="secondary" className="text-xs">
                        {SOURCE_LABEL[oo.source]}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-sm text-muted-foreground truncate max-w-[200px]">
                      {oo.reason ?? '—'}
                    </td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">
                      {format(new Date(oo.created_at), 'd MMM yyyy')}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-destructive"
                        onClick={() => deleteMutation.mutate(oo.opt_out_id)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add opt-out</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={handleSubmit((d) => addMutation.mutate(d))}
            className="space-y-4 pt-2"
          >
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input placeholder="person@example.com" {...register('email')} />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>LinkedIn URL</Label>
              <Input
                placeholder="https://linkedin.com/in/username"
                {...register('linkedin_url')}
              />
              {errors.linkedin_url && (
                <p className="text-xs text-destructive">{errors.linkedin_url.message}</p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Source</Label>
              <Select
                value={watch('source')}
                onValueChange={(v) => setValue('source', v as AddOptOutForm['source'])}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">Manual</SelectItem>
                  <SelectItem value="user_reply">User reply</SelectItem>
                  <SelectItem value="bounce_permanent">Permanent bounce</SelectItem>
                  <SelectItem value="gdpr_request">GDPR request</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Input placeholder="e.g. Requested via email reply" {...register('reason')} />
              {errors.reason && (
                <p className="text-xs text-destructive">{errors.reason.message}</p>
              )}
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addMutation.isPending}>
                {addMutation.isPending ? 'Adding…' : 'Add opt-out'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
